/**
 * Canonical domain types for the MACDM SPA — single source of truth.
 * Invariants: this module has zero outbound deps (pure types + tiny brand helpers); store/transport import from here, never the reverse.
 */

export type DebateTopology =
  | "round-robin"
  | "cross-fire"
  | "moderator-led"
  | "diverse-poles"
  | "solo-then-debate"
  // Kapsam-Güdümlü Soruşturma — pozisyonel sıra yerine boşluğa göre
  // konuşmacı seçer; soruda adı geçen her aktör için ayrılmış bir
  // "hücre" defterle takip edilir (bkz. `InvestigationPlan` altta).
  | "investigation";

export type AgentRole =
  // Moderator — always present, never user-selectable.
  | "moderator"
  // Legacy 2024-2025 council taxonomy. Still present in historical
  // DB rows so the union must accept them for replay/parsing.
  | "strategic-visionary"
  | "technical-authority"
  | "threat-analyst"
  | "contrarian"
  | "operational-commander"
  | "compliance-officer"
  // 2026 wizard taxonomy — richer role catalogue covering systems
  // thinking, risk, innovation, opposition, UX, and ethics.
  | "pragmatic-architect"
  | "risk-officer"
  | "innovator"
  | "devils-advocate"
  | "ux-advocate"
  | "ethics-steward"
  // 2026-08 genişletmesi — alandan bağımsız bilişsel duruşlar.
  // Backend `events.py::AgentRole` ile birebir eşleşmeli;
  // `test_codec_enum_parity.py` bunu CI'da denetliyor.
  | "financial-steward"
  | "people-advocate"
  | "customer-voice"
  | "evidence-auditor"
  | "tempo-strategist"
  | "simplifier"
  | "scale-planner"
  | "stakeholder-navigator"
  | "institutional-memory"
  | "systems-thinker";

export interface InformationAsymmetry {
  sources: string[];
  blindSpots: string[];
  confidenceCeiling: number;
}

export interface Persona {
  id: string;
  role: AgentRole;
  displayName: string;
  /**
   * Turkish corporate honorific resolved server-side (see orchestrator
   * `honorifics.py`). Optional with a "Bey" default so verdicts
   * persisted before the rollout still type-check. The FE renders
   * this verbatim — no client-side gender guessing.
   */
  honorific?: "Bey" | "Hanım";
  /**
   * Real-world defense-domain job title relevant to the question
   * (e.g. "C4ISR Mimarı", "Aviyonik Baş Mühendisi", "EW Uzmanı").
   *
   * This is the PRIMARY badge label shown in the UI; the abstract `role`
   * only conveys cognitive disposition (icon, color). Optional for backward
   * compatibility with manually-added personas.
   */
  expertise?: string;
  systemInstruction: string;
  asymmetry: InformationAsymmetry;
  voiceId: string;
  /**
   * Edge-TTS prosody overrides. All optional and persona-scoped — the
   * ServerTtsEngine forwards them per request so each persona gets
   * a distinct timbre profile (rate ±15%, pitch ±15Hz, volume ±20%).
   * Strings carry the raw edge-tts CLI value (e.g. "+5%", "-2Hz").
   */
  voiceRate?: string;
  voicePitch?: string;
  voiceVolume?: string;
  /**
   * LLM model id (catalog key, e.g. "gemma-3n-26b" / "gpt-4o-mini").
   * When set, the orchestrator routes THIS persona's turns to the named
   * model instead of the process-wide default. Omitted ⇒ default route.
   *
   * Surfaced in the persona edit drawer via `useModelsCatalog` so the
   * operator can pin a flagship model to a high-stakes seat (e.g. legal
   * counsel) and a fast model to chatter seats (cost optimisation).
   */
  modelId?: string;
  /**
   * Per-persona LLM sampling overrides. Each subfield is optional —
   * undefined means "fall back to the model catalogue default".
   * Surfaced in the persona edit drawer; the operator can dial a
   * Devil's Advocate hot (temp 1.0) and a Risk Officer cold (temp
   * 0.3) without touching anyone else's behaviour.
   */
  samplingOverrides?: SamplingOverrides;
  themeColor: string;
  seatIndex: number;
  avatarUrl?: string;
  /** Persona gender: "male" | "female". Selects the gender-mapped native tr-TR Edge-TTS voice. */
  gender?: "male" | "female";
}

export type TurnPhase =
  | "idle"
  | "retrieving"
  | "critiquing"
  | "speaking"
  | "yielding";

export type CritiqueSeverity = "minor" | "major" | "blocker";

export interface Critique {
  targetTurnId: string;
  flaw: string;
  severity: CritiqueSeverity;
}

export type TurnOutcome = "spoke" | "forfeited";

export interface ResearchSource {
  name: string;
  title: string;
  url: string;
  summary: string;
  published: string | null;
}

export interface TurnResearch {
  query: string;
  sources: ResearchSource[];
  elapsedMs: number;
  cacheHit: boolean;
  receivedAt: number;
}

/**
 * One RAG snippet citation injected into a speaker's prompt as
 * BACKGROUND DOCUMENTS. Mirrored on the verdict report's
 * "Belge Atıfları" bibliography so the user can audit which of their
 * uploaded documents the council actually leaned on.
 */
export interface RagCitation {
  docId: string;
  filename: string;
  chunkIdx: number;
  score: number;
  scope: "pool" | "private";
  excerpt: string;
}

export interface TurnRagBatch {
  query: string;
  citations: RagCitation[];
  receivedAt: number;
}

export interface Turn {
  id: string;
  speakerId: string;
  roundIndex: number;
  phase: TurnPhase;
  critique: Critique | null;
  tokens: string[];
  finalText: string | null;
  startedAt: number;
  endedAt: number | null;
  audioChunkIds: string[];
  outcome: TurnOutcome | null;
  forfeitReason?: string;
  research?: TurnResearch[];
  /** RAG snippet batches injected into this turn's prompt. */
  ragBatches?: TurnRagBatch[];
  /**
   * Engine-bounded summary of the model's internal reasoning channel
   * (vLLM `delta.reasoning`). Present only when the orchestrator is
   * configured with `llm_enable_thinking_for_turns=True` AND the
   * upstream model has a thinking channel (Gemma 3, Qwen3-Reasoner,
   * DeepSeek-R1, QwQ). Display as a collapsible block — never inline.
   */
  thinking?: { chars: number; summary: string };
  /**
   * İş Kolu E2 — citation-adherence audit: did this speaker actually
   * cite/name the research sources injected into their prompt?
   * `null`/absent when the turn had zero research sources to audit.
   * Previously computed server-side but only ever reached a
   * structured log line (SSH+journalctl required to see it) — now a
   * first-class, UI-visible per-turn signal.
   */
  citationAudit?: CitationAudit | null;
}

export interface CitationAudit {
  sourceCount: number;
  citedCount: number;
  adherenceScore: number;
  citedSources: string[];
  missingSources: string[];
  isLowAdherence: boolean;
  /** İş Kolu G3 — Grounded Verifier (numeric-claim edition). */
  numericClaims?: string[];
  ungroundedNumericClaims?: string[];
  /** İş Kolu BENCHMARK — fabricated-authority detection (Gartner/HBR/... icat edilmiş). */
  fabricatedAuthorities?: string[];
}

export interface ConsensusSnapshot {
  round: number;
  agreementMatrix: number[][];
  scalarScore: number;
  deadlock: boolean;
  shouldFinalize: boolean;
}

export interface SimulationOutcome {
  scenario: string;
  survived: boolean;
  rationale: string;
  failure_category?: string;
  missing_control?: string;
  severity?: "low" | "medium" | "high" | "critical";
  recommended_fix?: string;
}

/**
 * 2026 endüstri standartlı yapısal eylem planı tipleri.
 * Backend `domain/events.py` ile birebir hizalı (snake_case ↔ camelCase
 * Pydantic alias'larıyla). Yeni tartışmalar bu yapıda; eski tartışmalar
 * backend `_coerce_legacy_strings` validator'ü tarafından otomatik
 * upgrade ediliyor.
 *
 * Çerçeveler:
 *   • McKinsey 3-Horizons (5'e genişletilmiş: immediate + governance)
 *   • SMART criteria (zorunlu `successMetric`)
 *   • MoSCoW priority (P0/P1/P2)
 *   • RACI ownership (`owner` = R, `accountable` = A)
 *   • PMI PMBOK risk register
 */
export type ActionHorizon =
  | "immediate" // 0-2 hafta
  | "tactical" // 2-12 hafta
  | "strategic" // 3-12 ay
  | "architectural" // 12-36 ay
  | "governance"; // sürekli

export type ActionPriority = "P0" | "P1" | "P2";
export type ActionEffort = "XS" | "S" | "M" | "L" | "XL";

export interface ActionItem {
  title: string;
  rationale?: string;
  horizon: ActionHorizon;
  priority?: ActionPriority;
  effort?: ActionEffort;
  owner?: string | null;
  accountable?: string | null;
  successMetric: string;
  deadlineWindow?: string | null;
  dependencies?: string[];
  risks?: string[];
}

export interface RiskEntry {
  risk: string;
  likelihood?: "low" | "medium" | "high";
  impact?: "low" | "medium" | "high" | "critical";
  mitigation: string;
  owner?: string | null;
}

export interface KpiEntry {
  metric: string;
  target: string;
  cadence?: string | null;
}

export interface ActionPlanV2 {
  immediate: ActionItem[];
  tactical: ActionItem[];
  strategic: ActionItem[];
  architectural: ActionItem[];
  governance: ActionItem[];
  risks?: RiskEntry[];
  kpis?: KpiEntry[];
  criticalAssumptions?: string[];
}

/**
 * Geri uyumluluk yardımcısı — backend henüz upgrade edilmediyse veya
 * eski JSONB satırı doğrudan FE'ye düşerse: `string[]` tier'ları
 * minimum-viable `ActionItem` satırlarına çevirir, eksik tier'lara
 * boş dizi atar, alternatif `critical_assumptions` snake_case alanını
 * `criticalAssumptions`'a normalize eder.
 *
 * KORUYUCU: bu fonksiyon ASLA throw etmez — bozuk satırları sessizce
 * atar ki eski bir tartışmanın replay sayfası verdict yüzünden
 * çökmesin.
 */
export function coerceActionPlan(raw: unknown): ActionPlanV2 {
  const empty: ActionPlanV2 = {
    immediate: [], tactical: [], strategic: [], architectural: [], governance: [],
    risks: [], kpis: [], criticalAssumptions: [],
  };
  if (!raw || typeof raw !== "object") return empty;
  const r = raw as Record<string, unknown>;

  const TIERS: ActionHorizon[] = [
    "immediate", "tactical", "strategic", "architectural", "governance",
  ];

  const coerceTier = (tier: ActionHorizon, list: unknown): ActionItem[] => {
    if (!Array.isArray(list)) return [];
    const out: ActionItem[] = [];
    for (const it of list) {
      if (typeof it === "string") {
        const s = it.trim();
        if (!s) continue;
        out.push({
          title: s.length > 240 ? s.slice(0, 240) : s,
          horizon: tier,
          successMetric: "—",
          priority: "P1",
          effort: "M",
        });
      } else if (it && typeof it === "object") {
        const o = it as Record<string, unknown>;
        const title = typeof o.title === "string" ? o.title : "";
        if (!title) continue;
        // exactOptionalPropertyTypes: true → optional fields may NOT be
        // assigned `undefined`. Build the row in two steps so absent
        // fields stay absent (TS sees `key not present` ≠ `key: undefined`).
        const row: ActionItem = {
          title,
          horizon: (typeof o.horizon === "string" ? o.horizon : tier) as ActionHorizon,
          priority: (typeof o.priority === "string" ? o.priority : "P1") as ActionPriority,
          effort: (typeof o.effort === "string" ? o.effort : "M") as ActionEffort,
          owner: typeof o.owner === "string" ? o.owner : null,
          accountable: typeof o.accountable === "string" ? o.accountable : null,
          successMetric:
            (typeof o.successMetric === "string" && o.successMetric) ||
            (typeof o.success_metric === "string" && o.success_metric) ||
            "—",
          deadlineWindow:
            (typeof o.deadlineWindow === "string" && o.deadlineWindow) ||
            (typeof o.deadline_window === "string" && o.deadline_window) ||
            null,
          dependencies: Array.isArray(o.dependencies)
            ? (o.dependencies.filter((x) => typeof x === "string") as string[])
            : [],
          risks: Array.isArray(o.risks)
            ? (o.risks.filter((x) => typeof x === "string") as string[])
            : [],
        };
        if (typeof o.rationale === "string" && o.rationale) {
          row.rationale = o.rationale;
        }
        out.push(row);
      }
    }
    return out;
  };

  const out: ActionPlanV2 = {
    immediate: coerceTier("immediate", r.immediate),
    tactical: coerceTier("tactical", r.tactical),
    strategic: coerceTier("strategic", r.strategic),
    architectural: coerceTier("architectural", r.architectural),
    governance: coerceTier("governance", r.governance),
    risks: Array.isArray(r.risks)
      ? (r.risks.filter((x) => x && typeof x === "object") as RiskEntry[])
      : [],
    kpis: Array.isArray(r.kpis)
      ? (r.kpis.filter((x) => x && typeof x === "object") as KpiEntry[])
      : [],
    criticalAssumptions: Array.isArray(r.criticalAssumptions)
      ? (r.criticalAssumptions.filter((x) => typeof x === "string") as string[])
      : Array.isArray(r.critical_assumptions)
      ? (r.critical_assumptions.filter((x) => typeof x === "string") as string[])
      : [],
  };

  // Validate every tier ended up as an array (in case the source was
  // missing a key entirely). Only one path can null these out: an
  // explicit `tier: null` in the JSONB; we want `[]` instead.
  for (const t of TIERS) {
    if (!Array.isArray(out[t])) {
      (out as unknown as Record<string, unknown>)[t] = [];
    }
  }
  return out;
}

export interface Verdict {
  executiveSummary: string;
  dissentingLog: { agentId: string; concern: string; severity: CritiqueSeverity }[];
  actionPlan: ActionPlanV2;
  generatedAt: number;
  /**
   * AGI plan EPIC 2 — survivability fraction across the chaos
   * scenario bank, in [0,1]. Nullable because (a) historical
   * verdicts predate the rollout, (b) the cheap-model rollout is
   * best-effort and may be skipped on transient failure.
   */
  simulationScore?: number | null;
  simulationOutcomes?: SimulationOutcome[];
  baselineSimulationScore?: number | null;
  hardenedSimulationScore?: number | null;
  simulationRuns?: Record<string, unknown>[];
  resilienceStatus?: string | null;
  hardenedActionPlan?: ActionPlanV2 | null;
  confidenceMetrics?: Record<string, unknown> | null;
  boardBrief?: Record<string, unknown> | null;
  decisionMemory?: Record<string, unknown> | null;
  commentaryAnalysis?: string | null;
  commentaryCritique?: string | null;
  /**
   * İş Kolu E4 — traceable citation bibliography. Code-computed
   * (never LLM-generated) so it can never hallucinate a source that
   * wasn't genuinely fetched+named by a speaker. Empty for legacy
   * verdicts or debates where no source was ever cited.
   */
  evidenceBibliography?: EvidenceBibliographyEntry[];
  /**
   * Görev 24 — single FE-visible degradation flag. "degraded" means
   * at least one pipeline component (research/rag/verdict synthesis)
   * fell back, timed out, or errored this session. `null`/absent =
   * legacy verdict or a fully healthy run; render as "ok".
   */
  evidenceQuality?: "ok" | "degraded" | null;
  /**
   * Görev 18 — unified evidence-registry conflict list. Each entry is
   * a loosely-typed `{kind, detail, values}` record; empty = audit ran
   * and found nothing, not "audit didn't run".
   */
  evidenceConflicts?: Record<string, unknown>[];
  /**
   * Görev 20 — BLUF (Bottom Line Up Front): net recommendation + 2-3
   * reasons + main risk + confidence + reversal conditions. Shape is
   * intentionally loose (backend contract still evolving) — render
   * defensively with optional chaining, never assume every sub-field
   * is present.
   */
  bluf?: {
    recommendation?: string;
    reasons?: string[];
    mainRisk?: string;
    confidence?: number;
    /** Backend `Bluf.as_dict()` bunu `needToKnow` LİSTESİ olarak yollar.
     *  FE bir zamanlar `neededForCertainty` adında tekil bir metin
     *  arıyordu; böyle bir anahtar hiç gönderilmediği için "Emin olmak
     *  için" rozeti hiçbir kararda görünmedi. */
    needToKnow?: string[];
    reversalConditions?: string[];
  } | null;
  /** Görev 20 — immutable decision manifest (model/prompt/seed snapshot). */
  decisionManifest?: Record<string, unknown> | null;
  /**
   * Görev 21 — verdict state machine: answered|abstained|degraded|
   * review_required + human-approval sub-state. Loosely typed for the
   * same forward-compat reason as `bluf`.
   */
  /**
   * Anahtar adları backend'in `VerdictStatus.as_dict()`'iyle BİREBİR
   * aynı olmalı. Burada bir zamanlar `status`/`approvalStatus` yazıyordu;
   * backend ise `state` ve `humanReview.decision` gönderiyor. Hiçbiri
   * eşleşmediği için insan-onay kapısı (`ReviewActions`) tamamen
   * yazılmış olmasına rağmen ekranda HİÇ görünmedi.
   */
  verdictStatus?: {
    state?: "answered" | "abstained" | "degraded" | "review_required";
    reasons?: string[];
    reviewRequired?: boolean;
    signals?: Record<string, unknown>;
    humanReview?: {
      decision?: "approve" | "conditional" | "reject";
      note?: string;
      conditions?: string[];
      reviewedAt?: number;
    } | null;
  } | null;
  /** Görev 22 — decision-type classification (reversibility, door type). */
  decisionType?: Record<string, unknown> | null;
  /**
   * Kalite Planı — GET verdict endpoint'inin opsiyonel yeni alanları.
   * Backend snake_case yazar; legacy verdictlerde tamamen yoktur.
   * DOĞRUDAN OKUMA YASAK — `coerceCoverageMatrix` / `coerceEntityGraph`
   * / `coerceConsistencyFlags` helper'ları üzerinden erişin (REST
   * poll'u raw JSON'u `setVerdict`e verdiği için şekil garanti değil).
   * Alan yoksa ilgili sekme/panel HİÇ render edilmez.
   */
  coverage_matrix?: Record<string, unknown> | null;
  entity_graph?: Record<string, unknown> | null;
  consistency_flags?: unknown[] | null;
  /** Kapsam-Güdümlü Soruşturma nihai defteri — `coerceInvestigationPlan` ile okunur. */
  investigation_plan?: Record<string, unknown> | null;
}

export interface EvidenceBibliographyEntry {
  name: string;
  url: string;
  citedByTurnCount: number;
  citedBySpeakerIds: string[];
}

export interface ScheduledChunk {
  id: string;
  turnId: string;
  buffer: AudioBuffer;
  startAtCtxTime: number;
  wordTimings: { word: string; tStartMs: number; tEndMs: number }[];
}

export type SessionStatus =
  | "draft"
  | "provisioning"
  | "running"
  | "interrupted"
  | "finalized"
  | "error";

/* ----------------------------------------------------------------------
 * Discriminated union — SSE wire taxonomy. Mirrors backend §6 contract.
 * -------------------------------------------------------------------- */

export type DebateEvent =
  | { type: "persona.hydrate"; personas: Persona[]; seatOrder: string[] }
  | { type: "turn.start"; turnId: string; speakerId: string; roundIndex: number }
  | { type: "turn.phase"; turnId: string; phase: TurnPhase }
  | { type: "turn.critique"; turnId: string; critique: Critique }
  | { type: "turn.token"; turnId: string; token: string; seq: number }
  | {
      type: "turn.end";
      turnId: string;
      finalText: string;
      outcome: TurnOutcome;
      forfeitReason?: string;
      researchSources?: ResearchSource[];
      ragCitations?: RagCitation[];
      /** İş Kolu E2 — see `CitationAudit` below. */
      citationAudit?: CitationAudit | null;
    }
  | {
      type: "turn.research";
      turnId: string;
      query: string;
      sources: ResearchSource[];
      elapsedMs: number;
      cacheHit?: boolean;
    }
  | {
      type: "turn.rag";
      turnId: string;
      query: string;
      citations: RagCitation[];
    }
  | {
      // Internal chain-of-thought trace surfaced by the engine when
      // `MACDM_LLM_ENABLE_THINKING_FOR_TURNS=true`. The engine bounds
      // `summary` to ≤ 4 KB and never includes it in `final_text` /
      // TTS — strictly a UI affordance ("[uzman düşündü]" expander
      // under the bubble).
      type: "turn.thinking";
      turnId: string;
      chars: number;
      summary: string;
    }
  | { type: "consensus.update"; snapshot: ConsensusSnapshot }
  | { type: "moderator.interrupt"; pivot: string }
  | { type: "moderator.resolve" }
  | {
      type: "debate.finalize";
      // Konsey Cognitive Renaissance — Bölüm A1.
      // `max_rounds` reason yeni eklendi: tur bütçesi tükendi ama
      // gerçek consensus / deadlock olmadı. FE pill rengi info-blue.
      reason: "consensus" | "deadlock" | "user" | "max_rounds";
    }
  | { type: "verdict.ready"; verdict: Verdict }
  | {
      type: "resilience.progress";
      phase:
        | "simulation.baseline.started"
        | "simulation.baseline.completed"
        | "verdict.hardening.started"
        | "verdict.hardening.completed"
        | "simulation.hardened.started"
        | "simulation.hardened.completed";
      sessionId?: string;
      progress?: number;
      message?: string;
    }
  | { type: "stream.error"; code: string; message: string }
  | UserInterjection
  | SycophancyAlert
  | BrainUpdate
  | TurnPlanEvent
  | TurnVerifyEvent
  | SubagentResultEvent
  | ModeratorSummonEvent
  | CoverageUpdateEvent
  | EntityGraphEvent
  | InvestigationPlanEvent;

/* ----------------------------------------------------------------------
 * Konsey Cognitive Renaissance — Bölüm A2 + D2 (yeni SSE event'ler)
 * -------------------------------------------------------------------- */

export interface SycophancyAlert {
  type: "sycophancy.alert";
  round: number;
  score: number;
  dispersion: number;
  consecutive: number;
  devil_advocate_invited?: boolean;
}

export interface BrainUpdate {
  type: "brain.update";
  personaId: string;
  turnIndex: number;
  // Snapshot şeması `domain/brain.py:BrainState.model_dump()` ile uyumlu.
  // FE'de `brainSlice.applySnapshot` schema'sıyla normalise edilir.
  snapshot: Record<string, unknown>;
}

/* ----------------------------------------------------------------------
 * Kalite Planı — yeni SSE event taksonomisi (tamamı ADDITIVE).
 * Backend bu event'leri hiç göndermeyebilir; UI her birinin yokluğunda
 * eski davranışını korur. Hiçbir mevcut akış bu tiplere bağımlı değildir.
 * -------------------------------------------------------------------- */

/** Konuşmacının tur öncesi mikro-planı (`turn.plan`). */
export interface TurnPlanPayload {
  targetRequirements: string[];
  angle: string;
  evidenceToUse: string[];
  rebut: string | null;
}

export interface TurnPlanEvent {
  type: "turn.plan";
  turnId: string;
  speakerId: string;
  plan: TurnPlanPayload;
}

export type VerifyIssueSeverity = "low" | "med" | "high";

export interface VerifyIssue {
  kind: string;
  span: string;
  evidence: string;
  severity: VerifyIssueSeverity;
}

/** Tur sonrası doğrulama/heal sonucu (`turn.verify`). */
export interface TurnVerifyResult {
  ok: boolean;
  judgeUsed: boolean;
  material: boolean;
  issues: VerifyIssue[];
}

export interface TurnVerifyEvent {
  type: "turn.verify";
  turnId: string;
  speakerId: string;
  verdict: TurnVerifyResult;
  healJumps: number;
}

export type SubagentKind = "micro_expert" | "research" | "verifier" | "compute";

export interface SubagentComputation {
  code: string;
  stdout: string;
  outputHash: string;
}

export interface SubagentResultPayload {
  kind: string;
  ok: boolean;
  summary: string;
  evidenceUrls: string[];
  confidence: number;
  error: string | null;
  computation: SubagentComputation | null;
}

/** Arka plandaki alt-ajan (mikro-uzman/araştırma/doğrulayıcı/hesap) sonucu. */
export interface SubagentResultEvent {
  type: "subagent.result";
  kind: SubagentKind;
  result: SubagentResultPayload;
}

/** Moderatörün oturuma bilirkişi (tanık uzman) çağırması. */
export interface ModeratorSummonEvent {
  type: "moderator.summon";
  witnessId: string;
  witnessName: string;
  requirement: string;
}

export type CoverageRequirementStatus = "covered" | "partial" | "uncovered";

export interface CoverageRequirement {
  id: string;
  text: string;
  kind: string;
  status: CoverageRequirementStatus;
  evidenceTurnIds: string[];
}

/** Gereksinim kapsama matrisi — canlı `coverage.update` ile akar. */
export interface CoverageMatrix {
  deliverableType: string;
  score: number;
  requirements: CoverageRequirement[];
}

export interface CoverageUpdateEvent {
  type: "coverage.update";
  matrix: CoverageMatrix;
}

export interface EntityGraphNode {
  canonical: string;
  type: string;
  salience: number;
  reliability: number;
  evidenceUrls: string[];
  rejected: boolean;
}

export interface EntityGraphEdge {
  src: string;
  dst: string;
  relation: string;
  evidenceUrl: string;
  confidence: number;
}

/** Kanıt-bağlı varlık grafı — `entity.graph` snapshot'ı. */
export interface EntityGraph {
  sealed: boolean;
  nodes: EntityGraphNode[];
  edges: EntityGraphEdge[];
}

export interface EntityGraphEvent {
  type: "entity.graph";
  graph: EntityGraph;
}

/* ----------------------------------------------------------------------
 * Kapsam-Güdümlü Soruşturma — `investigation` topolojisinin hücre
 * defteri. Backend: `investigation_plan.InvestigationPlan.to_wire()`.
 * -------------------------------------------------------------------- */

export type InvestigationCellStatus = "open" | "partial" | "covered" | "no_evidence";

export interface InvestigationCell {
  id: string;
  entity: string;
  entityType: string;
  aspect: string;
  priority: number;
  status: InvestigationCellStatus;
  mustCover: boolean;
  openQuestions: string[];
  evidenceTurnIds: string[];
  citationUrls: string[];
}

/** Kapsam-güdümlü soruşturma defteri — kuruluş + her hücre güncellemesinde `investigation.plan` ile akar. */
export interface InvestigationPlan {
  query: string;
  coverageScore: number;
  cells: InvestigationCell[];
}

export interface InvestigationPlanEvent {
  type: "investigation.plan";
  plan: InvestigationPlan;
}

/* ----------------------------------------------------------------------
 * Kalite Planı — verdict JSON'unun opsiyonel `coverage_matrix` /
 * `entity_graph` / `consistency_flags` alanları için savunmacı coerce
 * helper'ları. REST verdict poll'u (VerdictPanel) raw JSON'u doğrudan
 * `setVerdict`e verdiği için bu alanların şekli Zod'dan geçmez; ASLA
 * throw etmeyen bu fonksiyonlar bozuk/eksik payload'da `null`/boş döner
 * ve ilgili sekme hiç render edilmez.
 * -------------------------------------------------------------------- */

function pickStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string") return v;
  }
  return "";
}

function pickNum(o: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return 0;
}

function pickStrArray(o: Record<string, unknown>, ...keys: string[]): string[] {
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) {
      return v.filter((x): x is string => typeof x === "string");
    }
  }
  return [];
}

export function coerceCoverageMatrix(raw: unknown): CoverageMatrix | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const reqsRaw = r.requirements;
  if (!Array.isArray(reqsRaw)) return null;
  const requirements: CoverageRequirement[] = [];
  for (const it of reqsRaw) {
    if (!it || typeof it !== "object") continue;
    const o = it as Record<string, unknown>;
    const text = pickStr(o, "text");
    if (!text) continue;
    const statusRaw = pickStr(o, "status");
    const status: CoverageRequirementStatus =
      statusRaw === "covered" || statusRaw === "partial" || statusRaw === "uncovered"
        ? statusRaw
        : "uncovered";
    requirements.push({
      id: pickStr(o, "id") || `req-${requirements.length}`,
      text,
      kind: pickStr(o, "kind"),
      status,
      evidenceTurnIds: pickStrArray(o, "evidenceTurnIds", "evidence_turn_ids"),
    });
  }
  if (requirements.length === 0) return null;
  return {
    deliverableType: pickStr(r, "deliverableType", "deliverable_type"),
    score: Math.max(0, Math.min(1, pickNum(r, "score"))),
    requirements,
  };
}

export function coerceEntityGraph(raw: unknown): EntityGraph | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const nodesRaw = Array.isArray(r.nodes) ? r.nodes : [];
  const edgesRaw = Array.isArray(r.edges) ? r.edges : [];
  const nodes: EntityGraphNode[] = [];
  for (const it of nodesRaw) {
    if (!it || typeof it !== "object") continue;
    const o = it as Record<string, unknown>;
    const canonical = pickStr(o, "canonical");
    if (!canonical) continue;
    nodes.push({
      canonical,
      type: pickStr(o, "type"),
      salience: Math.max(0, Math.min(1, pickNum(o, "salience"))),
      reliability: Math.max(0, Math.min(1, pickNum(o, "reliability"))),
      evidenceUrls: pickStrArray(o, "evidenceUrls", "evidence_urls"),
      rejected: o.rejected === true,
    });
  }
  const edges: EntityGraphEdge[] = [];
  for (const it of edgesRaw) {
    if (!it || typeof it !== "object") continue;
    const o = it as Record<string, unknown>;
    const src = pickStr(o, "src");
    const dst = pickStr(o, "dst");
    if (!src || !dst) continue;
    edges.push({
      src,
      dst,
      relation: pickStr(o, "relation"),
      evidenceUrl: pickStr(o, "evidenceUrl", "evidence_url"),
      confidence: Math.max(0, Math.min(1, pickNum(o, "confidence"))),
    });
  }
  if (nodes.length === 0 && edges.length === 0) return null;
  return { sealed: r.sealed === true, nodes, edges };
}

const _INVESTIGATION_CELL_STATUSES: readonly InvestigationCellStatus[] = [
  "open",
  "partial",
  "covered",
  "no_evidence",
];

/**
 * `investigation_plan` — Kapsam-Güdümlü Soruşturma hücre defteri.
 * `coverageMatrix`/`entityGraph` ile AYNI savunmacı sözleşme: bozuk/eksik
 * payload'da `null` döner, hiçbir alan throw etmez (REST verdict poll'u
 * raw JSON'u doğrudan verir, Zod'dan geçmez).
 */
export function coerceInvestigationPlan(raw: unknown): InvestigationPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const cellsRaw = r.cells;
  if (!Array.isArray(cellsRaw)) return null;
  const cells: InvestigationCell[] = [];
  for (const it of cellsRaw) {
    if (!it || typeof it !== "object") continue;
    const o = it as Record<string, unknown>;
    const entity = pickStr(o, "entity");
    if (!entity) continue;
    const statusRaw = pickStr(o, "status");
    const status: InvestigationCellStatus = (
      _INVESTIGATION_CELL_STATUSES as readonly string[]
    ).includes(statusRaw)
      ? (statusRaw as InvestigationCellStatus)
      : "open";
    cells.push({
      id: pickStr(o, "id") || `cell-${cells.length}`,
      entity,
      entityType: pickStr(o, "entityType", "entity_type") || "other",
      aspect: pickStr(o, "aspect"),
      priority: Math.max(0, Math.min(1, pickNum(o, "priority"))),
      status,
      mustCover: o.mustCover === true || o.must_cover === true,
      openQuestions: pickStrArray(o, "openQuestions", "open_questions"),
      evidenceTurnIds: pickStrArray(o, "evidenceTurnIds", "evidence_turn_ids"),
      citationUrls: pickStrArray(o, "citationUrls", "citation_urls"),
    });
  }
  if (cells.length === 0) return null;
  return {
    query: pickStr(r, "query"),
    coverageScore: Math.max(0, Math.min(1, pickNum(r, "coverageScore", "coverage_score"))),
    cells,
  };
}

/**
 * `consistency_flags` — gevşek tipli tutarlılık bulguları. Her satırı
 * görüntülenebilir bir kayda indirger; hiçbiri anlamlı değilse boş dizi
 * döner (panel o bölümü hiç göstermez).
 */
export function coerceConsistencyFlags(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is Record<string, unknown> => x !== null && typeof x === "object",
  );
}

/* ----------------------------------------------------------------------
 * Human-in-the-loop steering — every interjection round-trips through
 * SSE (`user.interject`, status: "queued" → "applied"). Keeping the
 * server as the source-of-truth lets us late-bind to a round boundary.
 * -------------------------------------------------------------------- */

export interface UserInterjection {
  type: "user.interject";
  id: string;
  text: string;
  receivedAt: number;
  /** Server-assigned round into which this message was injected. -1 (or null on the wire) while queued. */
  appliedAtRound: number;
  status: "queued" | "applied";
}

/* ----------------------------------------------------------------------
 * Model catalog — exposed by the orchestrator's `/api/debate/models`
 * endpoint. Wizard surfaces these so operators can pick a default
 * model + a fast-route fallback.
 * -------------------------------------------------------------------- */

/** Default sampling preset emitted alongside each model. */
export interface ModelSamplingDefaults {
  temperature: number;
  topP: number;
  maxTokens: number;
  presencePenalty: number;
  frequencyPenalty: number;
}

export interface ModelCatalogEntry {
  id: string;
  label: string;
  /**
   * Vendor / family — `gemma`, `qwen`, `llama`, `gpt`, …
   * Informational only; do NOT filter on this in UI groupings — use
   * `tier` instead. (Older bundles filtered the family directly,
   * which broke when the backend started emitting vendor strings.)
   */
  family: string;
  /**
   * UX grouping bucket — `fast` | `balanced` | `deep`. The persona
   * edit drawer renders one optgroup per tier so operators can
   * scan models by latency vs reasoning depth.
   */
  tier?: "fast" | "balanced" | "deep";
  /** Legacy alias, kept in lockstep with `tier`. */
  speedTier?: "fast" | "balanced" | "deep";
  description?: string;
  contextLen?: number;
  /** Persona role tilts the model excels at — `code`, `reasoning`, ... */
  fits?: string[];
  defaults?: ModelSamplingDefaults;
  /** Soft hint — how many simultaneous turns the model can fan out to. */
  maxConcurrency?: number;
}

/** Per-persona sampling overrides — mirror of backend `SamplingOverrides`. */
export interface SamplingOverrides {
  temperature?: number;
  topP?: number;
  maxTokens?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
}

/* ----------------------------------------------------------------------
 * Brand type: UserGestureToken.
 * Mintable ONLY from a real (isTrusted) DOM activation event, enforcing
 * the autoplay policy contract (see audioSlice.ensureAudioCtx).
 * -------------------------------------------------------------------- */

export type GestureOrigin = "click" | "touchend" | "pointerup" | "keydown";

export interface UserGestureToken {
  readonly __brand: "UserGestureToken";
  readonly value: string;
  readonly issuedAtMs: number;
  readonly origin: GestureOrigin;
}

const GESTURE_TTL_MS = 5_000;

export interface MintGestureInput {
  origin: GestureOrigin;
  isTrusted: boolean;
  timeStamp: number;
}

/**
 * Mint a UserGestureToken from a click / touchend / pointerup / keydown.
 *
 * Strict-mode rationale (legacy): browsers surface `event.isTrusted=true`
 * only for genuine user activation, not for `el.click()` / programmatic
 * dispatch. The autoplay policy contract enforced by `audioSlice` requires
 * a "real" gesture so the AudioContext can resume.
 *
 * Why we relax this check now: in production the FE is hosted inside a
 * Cursor Agent browser, embedded WebViews, and assistive-tech tooling,
 * all of which forward perfectly legitimate user clicks but synthesize
 * the React event so `nativeEvent.isTrusted` reads false. The previous
 * hard-throw silently killed `Tartışmayı Başlat` in those environments
 * (the FE caught the error, logged at debug-level, and the user saw
 * "hiçbir şey olmadı"). We now distinguish:
 *
 *   • genuine user-activation (always allowed) — best-effort detected via
 *     `navigator.userActivation.isActive` when available.
 *   • synthetic but click-like (allowed, soft warning) — anything that
 *     reaches us through a click handler is implicitly user-driven, even
 *     if React or a host shell repackaged it.
 *
 * This is safe wrt autoplay because the AudioContext is created INSIDE
 * the click frame and modern browsers honour either path. If they don't,
 * the worst-case is a deferred `audioCtx.resume()` on the next real click.
 */
export function mintUserGestureToken(input: MintGestureInput): UserGestureToken {
  if (!input.isTrusted) {
    const ua = (typeof navigator !== "undefined" ? (navigator as Navigator & {
      userActivation?: { isActive?: boolean; hasBeenActive?: boolean };
    }).userActivation : undefined);
    const userActivationOk = !!(ua?.isActive ?? ua?.hasBeenActive);
    if (!userActivationOk) {
      // Soft warn — don't throw. We've never observed a genuine misuse
      // in production telemetry; the throw caused 100% of the false
      // positives. Audio autoplay enforcement falls back to the next
      // call to `ensureAudioCtx()` if the synthetic gesture turns out
      // to be insufficient.
      // eslint-disable-next-line no-console
      console.warn(
        "[mintUserGestureToken] event.isTrusted=false and no userActivation; " +
          "minting anyway — audio autoplay may be deferred until next click.",
      );
    }
  }
  return {
    __brand: "UserGestureToken",
    value:
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `gesture-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
    issuedAtMs: Date.now(),
    origin: input.origin,
  };
}

export function isValidUserGestureToken(t: unknown): t is UserGestureToken {
  if (t === null || typeof t !== "object") return false;
  const obj = t as Record<string, unknown>;
  if (obj["__brand"] !== "UserGestureToken") return false;
  if (typeof obj["value"] !== "string") return false;
  const issued = obj["issuedAtMs"];
  if (typeof issued !== "number") return false;
  return Date.now() - issued <= GESTURE_TTL_MS;
}

/* ----------------------------------------------------------------------
 * Debate versioning types — cross-version comparison, persona snapshots.
 * -------------------------------------------------------------------- */

export interface DebateVersion {
  id: string;
  parent_debate_id: string | null;
  version_label: string | null;
  version_index: number;
  derived_from: "original" | "fork" | "duplicate";
  status: string;
  query: string | null;
  created_at: string | null;
  agent_count: number | null;
}

export interface DebatePersonaSnapshot {
  id: string;
  debate_id: string;
  seat_index: number;
  canonical_id: string | null;
  display_name: string;
  role: string;
  expertise: string | null;
  gender: string | null;
  honorific: string | null;
  system_instr: string;
  stance_summary: string | null;
  asymmetry: Record<string, unknown>;
  voice_id: string | null;
  theme_color: string | null;
  model_id: string | null;
  sampling_overrides: Record<string, unknown> | null;
}

export interface VerdictQualityScores {
  completeness: number;
  actionability: number;
  risk_coverage: number;
}

export interface ComparisonKeyDifference {
  aspect: string;
  debate_a: string;
  debate_b: string;
  winner: "a" | "b" | "tie";
}

export interface ComparisonPersonaImpact {
  persona: string;
  debate_a_influence: number;
  debate_b_influence: number;
  note?: string;
}

export interface ComparisonReport {
  summary: string;
  key_differences: ComparisonKeyDifference[];
  consensus_trajectory: {
    debate_a_scores: number[];
    debate_b_scores: number[];
  };
  persona_impact: ComparisonPersonaImpact[];
  strategic_insights: string[];
  recommendation: string;
  verdict_quality_scores: {
    debate_a: VerdictQualityScores;
    debate_b: VerdictQualityScores;
  };
}

export class AudioGestureRequiredError extends Error {
  override readonly name = "AudioGestureRequiredError";
  constructor(message = "AudioContext requires a fresh user gesture token.") {
    super(message);
    Object.setPrototypeOf(this, AudioGestureRequiredError.prototype);
  }
}

/**
 * Görev 5 — thrown by `sseClient.openDebateStream` when `POST /api/debate`
 * responds `409 {status:"needs_clarification"}`. NOT a transport failure:
 * the session was never admitted, nothing needs cleanup, and the caller
 * should surface `questions` to the user rather than a generic error
 * banner. Resubmitting the identical body with `clarification_answers` or
 * `skip_clarification=true` proceeds past the gate.
 */
export class NeedsClarificationError extends Error {
  override readonly name = "NeedsClarificationError";
  readonly questions: string[];
  readonly frame: Record<string, unknown> | null;
  constructor(
    questions: string[],
    frame: Record<string, unknown> | null,
    message = "Devam etmeden önce netleştirme gerekiyor.",
  ) {
    super(message);
    this.questions = questions;
    this.frame = frame;
    Object.setPrototypeOf(this, NeedsClarificationError.prototype);
  }
}
